import React, { useState,useRef, } from 'react';
import { evaluate, format, parse } from 'mathjs';
import './ScientificCalculator.css'; 
import { Minimize, Minus, MoveLeft, X } from 'lucide-react';
// // Assume you style color-coding here

const ScientificCalculator = ({onClose}) => {
   const [expression, setExpression] = useState('');
  const [result, setResult] = useState('0');
  const [memory, setMemory] = useState(0);
  const [angleMode, setAngleMode] = useState('DEG');
  const [showFormatted, setShowFormatted] = useState(false);

   const [position, setPosition] = useState({ x: 400, y: 100 });
    const isDragging = useRef(false);
    const offset = useRef({ x: 0, y: 0 });
    const boxRef = useRef();
  

  const toRadians = (deg) => deg * (Math.PI / 180);
  const toDegrees = (rad) => rad * (180 / Math.PI);

  const handleButtonClick = (value) => {
    setExpression((prev) => prev + value);
  };

  const handleBackspace = () => {
    setExpression((prev) => prev.slice(0, -1));
  };

  const handleClear = () => {
    setExpression('');
    setResult('');
  };

  const handleEvaluate = () => {
    try {
      let parsed = parse(expression);

      parsed.traverse(function (node) {
        if (
          node.isFunctionNode &&
          ['sin', 'cos', 'tan'].includes(node.fn.name) &&
          angleMode === 'DEG'
        ) {
          node.args[0] = new parse(`(${toRadians(evaluate(node.args[0].toString()))})`).compile().expr;
        }
        if (
          node.isFunctionNode &&
          ['asin', 'acos', 'atan'].includes(node.fn.name) &&
          angleMode === 'DEG'
        ) {
          const val = evaluate(node.toString());
          node = parse(`${toDegrees(val)}`);
        }
      });

      const evalResult = parsed.evaluate();
      if (typeof evalResult === 'number' && isFinite(evalResult)) {
        setResult(format(evalResult, { precision: 14 }));
      } else {
        setResult('Error');
      }
    } catch (error) {
      setResult('Error');
    }
  };

  const handleMemory = (action) => {
    const current = parseFloat(result) || 0;
    switch (action) {
      case 'MC': setMemory(0); break;
      case 'MS': setMemory(current); break;
      case 'MR': setExpression(memory.toString()); break;
      case 'M+': setMemory(memory + current); break;
      case 'M-': setMemory(memory - current); break;
      default: break;
    }
  };

  const insertFunction = (func) => {
    setExpression((prev) => `${func}(${prev})`);
  };

  const insertOperation = (type) => {
    switch (type) {
      case 'sqrt': setExpression((prev) => `sqrt(${prev})`); break;
      case 'cbrt': setExpression((prev) => `(${prev})^(1/3)`); break;
      case 'inv': setExpression((prev) => `1/(${prev})`); break;
      case 'mod': setExpression((prev) => `${prev}%`); break;
      case 'log2': setExpression((prev) => `log(${prev}, 2)`); break;
      case 'logxy': setExpression((prev) => `${prev},`); break; // user can enter log(x, y)
      case 'yroot': setExpression((prev) => `${prev}^(1/`); break; // append yroot(x) as x^(1/y)
      case 'abs': setExpression((prev) => `abs(${prev})`); break;
      case 'pi': setExpression((prev) => `${prev}pi`); break;
      case 'e': setExpression((prev) => `${prev}e`); break;
      case 'fact': setExpression((prev) => `${prev}!`); break;
      default: break;
    }
  };

  const formatExpression = (exp) => {
    return exp.replace(/\^2/g, '²')
              .replace(/\^3/g, '³')
              .replace(/\^/g, 'ⁿ')
              .replace(/sqrt/g, '√')
              .replace(/asinh/g, 'sinh⁻¹')
              .replace(/acosh/g, 'cosh⁻¹')
              .replace(/atanh/g, 'tanh⁻¹')
              .replace(/asin/g, 'sin⁻¹')
              .replace(/acos/g, 'cos⁻¹')
              .replace(/atan/g, 'tan⁻¹')
              .replace(/log\(([^,]+),\s*2\)/g, 'log₂($1)')
              .replace(/log\(([^,]+),\s*([^)]+)\)/g, 'log₍$2₎($1)')
              .replace(/abs\(([^)]+)\)/g, '|$1|')
              .replace(/([^)]+)\^\(1\/([^)]+)\)/g, '√₍$2₎($1)')
              .replace(/\(([^)]+)\)\^\(1\/3\)/g, '∛($1)')
              .replace(/pi/g, 'π')
              .replace(/e/g, '𝑒')
              .replace(/([0-9]+)!/g, '$1!');
  };

  const colorizeExpression = (exp) => {
    const parts = exp.split(/([\d\.]+|[+\-*/%^()|!])/g).filter(Boolean);
    return parts.map((part, i) => {
      if (/^\d/.test(part)) {
        return <span key={i} className="num">{part}</span>;
      } else if (/^[a-zA-Z⁻¹√₍₎₁₂₃₄₅₆₇₈₉₀π𝑒∛|]+$/.test(part)) {
        return <span key={i} className="func">{part}</span>;
      } else {
        return <span key={i} className="op">{part}</span>;
      }
    });
  };


    const handleMouseDown = (e) => {
      isDragging.current = true;
      const rect = boxRef?.current.getBoundingClientRect();
      offset.current = {
        x: e.clientX - rect?.left,
        y: e.clientY - rect?.top,
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };
  
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
  
      const box = boxRef?.current;
      const boxWidth = box?.offsetWidth;
      const boxHeight = box?.offsetHeight;
  
      const newX = Math.min(
        Math.max(0, e.clientX - offset?.current.x),
        window?.innerWidth - boxWidth
      );
      const newY = Math.min(
        Math.max(0, e.clientY - offset.current.y),
        window?.innerHeight - boxHeight
      );
  
      setPosition({ x: newX, y: newY });
    };
  
    const handleMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  
  return (
    <div
    
    >
<div className='sciCalOuter'   ref={boxRef}
      onMouseDown={handleMouseDown}
      style={{
        position: 'fixed',
        left: position?.x,
        top: position?.y,
      
        
        userSelect: 'none',zIndex:"999999999999999999"  
      }}>
  <div className='sciCalTop' >
   <div className='sciCalTopTitle' >Scientific Calculator</div>
   <div className='sciCalTopBlank' ></div>
   <div className='sciCalTopHelp' >Help</div>
   <div className='sciCalTopbtn' ><Minus style={{position:"relative",marginTop:"5px"}}   color={"#fff"} size={25}/> </div>
   <div className='sciCalTopbtn' onClick={()=>{onClose()}}><X style={{position:"relative",marginTop:"5px"}}   color={"#fff"} size={25}/></div>
</div>
<div className='sciCalInput'> {colorizeExpression(formatExpression(expression))}</div>
<div className='sciCalInput'>{result} </div>
<div className='sciCalButtonGridOne'> 
    <button  className='sciCalButton'>MOD</button>  <div style={{display:"flex",justifyContent: "center",alignItems: "center",fontSize: "11px"}}><input name="angleMode1"     type="radio" checked={angleMode=="DEG"?true:false} onChange={() => setAngleMode( 'DEG')}/>Deg</div>
      <div style={{display:"flex",justifyContent: "center",alignItems: "center",fontSize: "11px"}}><input name="angleMode1"   type="radio" checked={angleMode=="RAD"?true:false} onChange={() => setAngleMode('RAD' )}/>Rad</div>
      <div></div> <div></div> <div></div>
      <button  className='sciCalButton' onClick={() => handleMemory('MC')}>MC</button>
        <button  className='sciCalButton' onClick={() => handleMemory('MR')}>MR</button>
        <button  className='sciCalButton' onClick={() => handleMemory('MS')}>MS</button>
        <button  className='sciCalButton' onClick={() => handleMemory('M+')}>M+</button>
        <button  className='sciCalButton' onClick={() => handleMemory('M-')}>M-</button>

    {/* {angleMode} */}
</div>
<div className='sciCalButtonGridTwo'> 
        <button  className='sciCalButton' onClick={() => insertFunction('sinh')}>sinh</button>
        <button  className='sciCalButton' onClick={() => insertFunction('cosh')}>cosh</button>
        <button  className='sciCalButton' onClick={() => insertFunction('tanh')}>tanh</button>

        <button  className='sciCalButton' onClick={() => insertFunction('exp')}>Exp</button>

        <button  className='sciCalButton' onClick={() => handleButtonClick('(')}>(</button>
        <button  className='sciCalButton'onClick={() => handleButtonClick(')')}>)</button>
<div className='sciCalButtonRed' onClick={handleBackspace}> <MoveLeft style={{position:"relative",}}   color={"#fff"} size={20}/> </div> 

        <button className='sciCalButtonRedSmall' onClick={handleClear}>C</button>
        <button className='sciCalButtonRedSmall' onClick={() => handleButtonClick('+')}>+</button>


        <button className='sciCalButton' onClick={() => insertOperation('sqrt')}>√x</button>
</div>
<div className='sciCalButtonGridOne'> 
  <button className='sciCalButton' onClick={() => insertFunction('asinh')}>sinh⁻¹</button>
        <button className='sciCalButton'onClick={() => insertFunction('acosh')}>cosh⁻¹</button>
        <button  className='sciCalButton' onClick={() => insertFunction('atanh')}>tanh⁻¹</button>

        <button className='sciCalButton'onClick={() => insertOperation('log2')}>log₂(x)</button>
        
        <button className='sciCalButton'onClick={() => insertFunction('log')}>ln</button>
        <button className='sciCalButton'onClick={() => insertFunction('log10')}>log</button>

        <button className='sciCalButton' onClick={() => handleButtonClick('7')}>7</button>
        <button className='sciCalButton'onClick={() => handleButtonClick('8')}>8</button>
        <button className='sciCalButton'onClick={() => handleButtonClick('9')}>9</button>
        
        <button  className='sciCalButton'onClick={() => handleButtonClick('/')}>/</button>
        <button  className='sciCalButton'onClick={() => handleButtonClick('%')}>%</button>
</div><div className='sciCalButtonGridOne'>
 <button className='sciCalButton' onClick={() => insertOperation('pi')}>π</button>
        <button className='sciCalButton' onClick={() => insertOperation('e')}>e</button>
        <button className='sciCalButton' onClick={() => insertOperation('fact')}>n!</button>
        <button  className='sciCalButton'  onClick={() => insertFunction('log')}>log<sub>y</sub><sup>x</sup></button>
<button className='sciCalButton' onClick={() => handleButtonClick('e^(')}>e<sup>x</sup></button>
<button className='sciCalButton' onClick={() => handleButtonClick('10^(')}>10<sup>x</sup></button>

        <button className='sciCalButton'  onClick={() => handleButtonClick('4')}>4</button>
        <button className='sciCalButton' onClick={() => handleButtonClick('5')}>5</button>
        <button className='sciCalButton' onClick={() => handleButtonClick('6')}>6</button>
        
        <button className='sciCalButton'  onClick={() => handleButtonClick('*')}>×</button>
        <button className='sciCalButton'  onClick={() => insertOperation('inv')}>1/x</button>
</div>
<div className='sciCalButtonGridThree'>
  <div>
  <div className='sciCalButtonGridThreeInn1'>
  
        <button className='sciCalButton' onClick={() => insertFunction('sin')}>sin</button>
        <button className='sciCalButton' onClick={() => insertFunction('cos')}>cos</button>
        <button className='sciCalButton' onClick={() => insertFunction('tan')}>tan</button>

        <button className='sciCalButton' onClick={() => handleButtonClick('^')}>xʸ</button>
        <button className='sciCalButton' onClick={() => handleButtonClick('^3')}>x³</button>
        <button className='sciCalButton' onClick={() => handleButtonClick('^2')}>x²</button>
        
        <button  className='sciCalButton' onClick={() => handleButtonClick('1')}>1</button>
        <button  className='sciCalButton'onClick={() => handleButtonClick('2')}>2</button>
        <button  className='sciCalButton'onClick={() => handleButtonClick('3')}>3</button>
        
        <button  className='sciCalButton' onClick={() => handleButtonClick('-')}>−</button>
</div> 
<div className='sciCalButtonGridThreeInn2'>
  
        <button className='sciCalButton' onClick={() => insertFunction('asin')}>sin⁻¹</button>
        <button className='sciCalButton' onClick={() => insertFunction('acos')}>cos⁻¹</button>
        <button className='sciCalButton' onClick={() => insertFunction('atan')}>tan⁻¹</button>
        
        <button className='sciCalButton' onClick={() => insertOperation('yroot')}><sup>y</sup>√x</button>
        
        <button className='sciCalButton' onClick={() => insertOperation('cbrt')}>∛x</button>
        <button className='sciCalButton' onClick={() => insertOperation('abs')}>|x|</button>
        
        <button className='sciCalButtonBig' onClick={() => handleButtonClick('0')}>0</button>
        <button className='sciCalButton' onClick={() => handleButtonClick('.')}>.</button>
        
        <button className='sciCalButton' onClick={() => handleButtonClick('+')}>+</button>
</div>
</div><div> <button className='sciCalButtonBigHor' onClick={handleEvaluate}>=</button></div></div>
</div><div>
  
</div>



    </div>
  );
};

export default ScientificCalculator;
